import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import MobileBottomBar from "./MobileBottomBar";
import "./CSS/MainContainerMobile.css";
import ChatInterface from "./Current Training/Components/ChatInterface";
import AIAnalysis from "./Current Training/Components/AIAnalysis";
import ProcessDiagram from "./Current Training/Components/ProcessDiagram";
import VideoUpload from "./Current Training/Components/VideoUpload";
import HighlightsTimeline from "./Current Training/Components/HighlightsTimeline";
import TranscriptionWindow from "./Current Training/Components/TranscriptionWindow";
import { io } from "socket.io-client";
import MobileHomePage from "./MobileHomePage";
import {
  getCachedRestorePayload,
  setCachedRestorePayload,
} from "../offline/sessionOffline";

const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MainContainerMobile = ({ userId, onAppReload, profile, setShowNameModal, isGuest, isMobile, refreshTrigger, setProfile, setIsDemo, isDemo, isOnline }) => {
  const [selectedPage, setSelectedPage] = useState("home");
  const [videoTime, setVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [newVideoUploaded, setNewVideoUploaded] = useState(false);
  const [videoId, setVideoId] = useState(null);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [showTranscription, setShowTranscription] = useState(false);
  const [restoredChatMessages, setRestoredChatMessages] = useState([]);
  const [restoredTranscriptions, setRestoredTranscriptions] = useState([]);
  const [video, setVideo] = useState(null);
  const [showRestoreList, setShowRestoreList] = useState(false);
  const [hasRespondedToIntro, setHasRespondedToIntro] = useState(false);
  const [slpConversations, setSlpConversations] = useState({});
  const scrollContainerRef = useRef(null);
  const hasRestored = useRef(false);
  const [analysisReady, setAnalysisReady] = useState(false);
  const visibleAnalysisResults = useMemo(
    () => analysisResults.filter((r) => r.is_deleted !== true),
    [analysisResults]
  );

  const gotoReview = () => setSelectedPage("review");

  const handleVideoTimeUpdate = (time, duration) => {
    setVideoTime(time);
    setVideoDuration(duration);
  };

  const seekToTime = (time) => {
    const video = document.querySelector("video");
    if (video) {
      video.currentTime = time;
    }
  };

  useEffect(() => {
    if (!videoId) return;

    const socket = io(REACT_APP_API_BASE_URL);

    socket.on("analysis_progress", (data) => {
      if (data.videoId !== videoId) return;
      setAnalysisResults(prev => [...prev, data]);

      if ((isDemo || isGuest) && !analysisReady) setAnalysisReady(true);
    });

    socket.on("analysis_ready", ({ videoId: id }) => {
      if (id === videoId) setAnalysisReady(true);
    });

    return () => socket.disconnect();
  }, [videoId, analysisReady, isDemo, isGuest]);

  const handleTranscriptionToggle = () => {
    setShowTranscription((prev) => !prev);
  };

  const handleSessionRestore = useCallback(async (session) => {
    const testVideoId = session.id;
    const applyRestoreData = (data) => {
      setHasRespondedToIntro(true);
      setAnalysisReady(Boolean(data.session?.analysis_ready));
      setVideoId(testVideoId);
      const mappedSegments = (data.analysisSegments || []).map(seg => ({
        "Begin-End": seg.begin_end,
        "Fidelity Score": seg.fidelity_score,
        "Feedback": seg.ai_reasoning,
        "Strategy": seg.strategy,
        "Child Response": seg.child_response,
        "Child Transcript": seg.all_sentences,
        is_deleted: seg.is_deleted,
      }));
      setAnalysisResults(mappedSegments);
      const aiMessages = [];
      const slpConvoMap = {};

      (data.chatHistory || []).forEach(msg => {
        const base = {
          text: msg.message,
          sender:
            msg.role === "assistant" ? "bot"
            : msg.role === "slp"      ? "slp"
            : msg.role === "user-slp" ? "user"
            : "user",
        };

        if (msg.quick_replies) {
          base.options = msg.quick_replies.map(label => ({
            label,
            value: label,
          }));
        }

        if (msg.slp_id) {
          if (!slpConvoMap[msg.slp_id]) {
            slpConvoMap[msg.slp_id] = [];
          }
          slpConvoMap[msg.slp_id].push(base);
        } else {
          aiMessages.push(base);
        }
      });

      setRestoredChatMessages(aiMessages);
      setSlpConversations(slpConvoMap);

      setRestoredTranscriptions(
        (data.transcriptions || []).map(t => ({
          start: t.start_time,
          end: t.end_time,
          text: t.text,
          speaker: t.speaker,
        }))
      );
      hasRestored.current ? setSelectedPage("review") : setSelectedPage("chat");
      setShowRestoreList(false);

      if (data.session?.is_demo) {
        setVideo(process.env.PUBLIC_URL + "/demo/demo.MOV");
      } else {
        setVideo(null);
      }
    };

    if (!isOnline) {
      const cached = getCachedRestorePayload(testVideoId);
      if (cached?.payload) {
        applyRestoreData(cached.payload);
      } else {
        alert("This session is not cached yet. Open it once while online to use offline restore.");
      }
      return;
    }

    try {
      const res = await fetch(`${REACT_APP_API_BASE_URL}/api/restore-session/${testVideoId}`);
      if (!res.ok) throw new Error("Failed to restore");
      const data = await res.json();
      setCachedRestorePayload(testVideoId, data);
      applyRestoreData(data);
      if (!data.session?.is_demo && data.objectKey) {
        const safeKey = encodeURIComponent(data.objectKey);
        setVideo(`${REACT_APP_API_BASE_URL}/api/video/${safeKey}`);
      }
    } catch (err) {
      console.error("❌ Error restoring session:", err);
      const cached = getCachedRestorePayload(testVideoId);
      if (cached?.payload) {
        applyRestoreData(cached.payload);
      }
    }
  }, [isOnline]);  

  useEffect(() => {
    const params    = new URLSearchParams(window.location.search);
    const restoreId = params.get("restore");
    if (!restoreId || hasRestored.current) return;

    hasRestored.current = true;
    handleSessionRestore({ id: restoreId });
    window.history.replaceState({}, "", "/Project-Demo");
  }, [handleSessionRestore]);

  return (
    <div className="home-mobile-wrapper">
      <div className="mobile-content" ref={scrollContainerRef}>
      <div
          className="home-container-mobile fade-in"
          style={{
            display: selectedPage === "home" ? "block" : "none",
          }}
        >
          <MobileHomePage setShowNameModal={setShowNameModal} profile={profile} userId={userId} isMobile={isMobile} showRestoreList={showRestoreList} setShowRestoreList={setShowRestoreList} onRestore={handleSessionRestore} scrollContainerRef={scrollContainerRef} setProfile={setProfile} isOnline={isOnline}/>
        </div>
        <div
          className="chat-container-mobile fade-in"
          style={{
            display: selectedPage === "chat" ? "block" : "none",
          }}
        >
          <ChatInterface
            videoTime={videoTime}
            videoDuration={videoDuration}
            newVideoUploaded={newVideoUploaded}
            setNewVideoUploaded={setNewVideoUploaded}
            seekToTime={seekToTime}
            videoId={videoId}
            profile={profile}
            isOnline={isOnline}
            isGuest={isGuest}
            refreshTrigger={refreshTrigger}
            restoredChatMessages={restoredChatMessages}
            onRequestVideoUpload={gotoReview}
            hasRespondedToIntro={hasRespondedToIntro}
            setHasRespondedToIntro={setHasRespondedToIntro}
            slpConversations={slpConversations}
            setSlpConversations={setSlpConversations}
          />
        </div>
        <div
          className="review-container-mobile fade-in"
          style={{
            display: selectedPage === "review" ? "block" : "none",
          }}
        >
          <VideoUpload
            onAppReload={onAppReload}
            onVideoTimeUpdate={handleVideoTimeUpdate}
            setNewVideoUploaded={setNewVideoUploaded}
            setVideoId={setVideoId}
            videoId={videoId}
            videoDuration={videoDuration}
            seekToTime={seekToTime}
            handleTranscription={handleTranscriptionToggle}
            profile={profile}
            video={video}
            setVideo={setVideo}
            setIsDemo={setIsDemo}
          />
          {videoId && analysisReady && visibleAnalysisResults.length > 0 && (
            <HighlightsTimeline
              videoId={videoId}
              videoDuration={videoDuration}
              seekToTime={seekToTime}
              analysisResults={visibleAnalysisResults}
            />
          )}
          <div
            className={`transcription-container ${
              showTranscription ? "visible" : ""
            }`}
          >
            {showTranscription && (
              <TranscriptionWindow
                videoId={videoId}
                videoTime={videoTime}
                seekToTime={seekToTime}
                isMobile={isMobile}
                restoredTranscriptions={restoredTranscriptions}
              />
            )}
          </div>
          {videoId && (
            <AIAnalysis
              analysisResults={visibleAnalysisResults}
              videoId={videoId}
              seekToTime={seekToTime}
              isMobile={isMobile}
              profile={profile}
              analysisReady={analysisReady}
              isDemo={isDemo}
              isGuest={isGuest}
            />
          )}
        </div>
        <div
          className="diagram-container-mobile fade-in"
          style={{
            display: selectedPage === "diagram" ? "block" : "none",
          }}
        >
          {" "}
          <ProcessDiagram />
        </div>
      </div>
      <MobileBottomBar
        selectedPage={selectedPage}
        setSelectedPage={setSelectedPage}
        setShowNameModal={setShowNameModal}
      />
    </div>
  );
};

export default MainContainerMobile;
